<template>
  <div
    class="lr-map"
    data-scroll-results
  >
    <p
      v-once
      class="lr-map-intro"
    >
      {{ locatorResultsIntro }}
    </p>
    <h2
      v-once
      class="lr-map-title"
    >
      {{ locatorResultsTitle }}
    </h2>

    <div class="lr-map-container">
      <div class="lr-map-container-left">
        <div class="lr-map-results">
          <nav class="lr-map-results-nav">
            <kf-button
              v-for="careType in careTypes"
              :key="careType.childcareTypeKey"
              class="lr-map-results-btn"
              :class="{ 'is-active': tabIsActive.childcareTypeKey === careType.childcareTypeKey }"
              @click="handleMapResultsSwitcher(careType.childcareTypeKey)"
            >
              {{ careType.childcareTypeName }}
            </kf-button>
          </nav>
          <div
            class="lr-map-results-list-container"
            :class="{ 'is-hidden': mapIsActive }"
          >
            <div
              v-if="
                !isLoadingResults &&
                  locationResultsSplitted[tabIsActive.childcareTypeKey] &&
                  locationResultsSplitted[tabIsActive.childcareTypeKey].length
              "
            >
              <div
                v-for="location in locationResultsSplitted[tabIsActive.childcareTypeKey]"
                :key="location.id"
                class="lr-map-results-list is-visible"
              >
                <div
                  v-for="venue in location.venues"
                  :key="venue.id"
                >
                  <div
                    v-if="isActiveCareType(venue)"
                    class="lr-map-results-list-item"
                    :class="{ 'is-selected': location.isSelected }"
                    role="button"
                    tabindex="0"
                    @mouseenter="selectMarker(location)"
                    @focus="selectMarker(location)"
                    @mouseleave="deselectMarker(location)"
                    @blur="deselectMarker(location)"
                  >
                    <h4>
                      <a
                        :href="getLocalizedUrl(venue)"
                        class="lr-map-results-link"
                        @click.prevent="manageOtherLanguageModal(venue, location)"
                      >
                        <div class="lr-map-results-inner-title">
                          <arrow-right class="icon" />
                          {{ location.name }}
                          <span
                            v-if="location.distance"
                            class="lr-map-results-distance"
                          >
                            {{ location.distance }} km
                          </span>
                        </div>

                        <div
                          v-if="venue.photoDesktopFirst || venue.photoDesktopLast"
                          class="lr-map-results-images"
                        >
                          <div class="lr-map-results-image-container">
                            <img
                              v-if="venue.photoDesktopFirst"
                              :src="venue.photoDesktopFirst"
                              :alt="location.name"
                              class="lr-map-results-image"
                            >
                          </div>
                          <div class="lr-map-results-image-container">
                            <img
                              v-if="venue.photoDesktopLast"
                              :src="venue.photoDesktopLast"
                              :alt="location.name"
                              class="lr-map-results-image"
                            >
                          </div>
                        </div>
                      </a>
                    </h4>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="isLoadingResults"
              class="lr-map-results-list-item"
            >
              {{ i18n.locatorResultsMap.locatorLoadingText.label }}
            </div>
            <div
              v-else
              class="lr-map-results-list-item"
            >
              {{ i18n.locatorResultsMap.locatorNoResultsText.label }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="lr-map-container-right"
        :class="{ 'is-hidden': !mapIsActive }"
      >
        <l-map
          v-if="map.showMap"
          ref="map"
          :options="map.mapOptions"
          :bounds="map.bounds"
          style="height: 100%"
          track-resize="true"
          @update:bounds="boundsUpdate"
        >
          <l-tile-layer
            :url="map.url"
            :attribution="map.attribution"
          />
          <l-marker-cluster
            ref="clustersRef"
            :options="map.clusterOptions"
          >
            <l-marker
              v-for="marker in locationMarkerData"
              :key="marker.id"
              ref="markersRef"
              :set="(relatedVenue = getVenueBasedOnCareType(marker))"
              :visible="showMarkerIfCorrectCaretype(marker)"
              :lat-lng="{ lng: marker.geoLocation.longitude, lat: marker.geoLocation.latitude }"
              :icon="map.icon"
              @click="selectLocation(marker)"
              @keydown="selectLocation(marker)"
            >
              <l-popup :options="{ closeButton: false }">
                <a
                  class="leaflet-popup-close-button"
                  href="#close"
                  @click.prevent="closeLeafletPopup()"
                >×</a>
                <div class="marker-popup">
                  <a
                    :href="getLocalizedUrl(relatedVenue)"
                    class="marker-popup-link"
                    @click.prevent="
                      manageOtherLanguageModal(getVenueBasedOnCareType(marker), marker)
                    "
                  >
                    <h5 class="marker-popup-title">
                      {{ marker.name }}
                    </h5>
                    <div class="marker-popup-meta">
                      <span class="marker-popup-city">
                        {{ marker.address.city }}
                      </span>
                      <span
                        v-if="marker.distance"
                        class="marker-popup-distance"
                      >
                        {{ marker.distance }} km
                      </span>
                    </div>
                    <img
                      v-if="relatedVenue.photoDesktopFirst"
                      :src="relatedVenue.photoDesktopFirst"
                      alt=""
                      class="marker-popup-image"
                    >
                  </a>
                </div>
              </l-popup>
            </l-marker>
          </l-marker-cluster>
          <l-marker
            key="current-location"
            :lat-lng="currentPosition"
            :icon="map.locationIcon"
            :opacity="map.showCurrentLocation"
          />
        </l-map>
      </div>
    </div>

    <kf-button
      class="btn lr-map-toggle-btn"
      type="primary"
      @click="toggleMobileMap"
    >
      <span v-if="mapIsActive">
        {{ i18n.locatorResultsMap.showListView.label }}
      </span>
      <span v-else>
        {{ i18n.locatorResultsMap.showMapView.label }}
      </span>
    </kf-button>

    <kf-loader v-if="isLoadingMap || isLoadingResults" />
    <LanguageNotAvailableModal
      :is-open="languageNotAvailableModalIsOpen"
      :venue="languageNotAvailableModalVenueObject"
      @close="setLanguageNotAvailableModal"
    />
  </div>
</template>

<script>
import KfButton from '@/components/base/button/Button.vue';
import LanguageNotAvailableModal from '@/components/language-selector/LanguageNotAvailableModal.vue';
import KfLoader from '@/components/loader/Loader.vue';
import { setDayCareString, trackEvent } from '@/lib/gtm/trackEvent';
import axios from 'axios';
import * as LeafLet from 'leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet/dist/leaflet.css';
import { LMap, LMarker, LPopup, LTileLayer } from 'vue2-leaflet';
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';

delete LeafLet.Icon.Default.prototype._getIconUrl;
LeafLet.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('@/assets/svg/map/map-marker.svg?inline'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const normalIcon = new LeafLet.icon({
  iconUrl: require('@/assets/svg/map/map-marker.svg?inline'),
  iconSize: [52, 58],
  iconAnchor: [16, 37],
  popupAnchor: [11, -35],
});

const bigIcon = new LeafLet.icon({
  iconUrl: require('@/assets/svg/map/map-marker.svg?inline'),
  iconSize: [82, 88],
  iconAnchor: [31, 67],
  popupAnchor: [11, -55],
});

const currentLocationIcon = new LeafLet.icon({
  iconUrl: require('@/assets/images/currentLocationIcon.png'),
  iconSize: [40, 40],
  iconAnchor: [16, 37],
});

export default {
  name: 'KfLocatorResultsMap',
  components: {
    KfButton,
    KfLoader,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LanguageNotAvailableModal,
    'l-marker-cluster': Vue2LeafletMarkerCluster,
    ArrowRight: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ArrowRight.vue'),
  },
  props: {
    i18n: {
      type: Object,
      default: () => {},
    },
    locatorResultsTitle: {
      type: String,
      default: '',
    },
    locatorResultsIntro: {
      type: String,
      default: '',
    },
    locatorPostalCode: {
      type: String,
      default: '',
    },
    apiEndpoint: {
      type: String,
      default: null,
    },
    stadiaMapsApiKey: {
      type: String,
      default: process.env.VUE_APP_STADIA_MAPS_API_KEY,
    },
    latlng: {
      type: Object,
      default: () => ({}),
    },
    areaId: {
      type: Number,
      default: null,
    },
    careTypes: {
      type: Array,
      default: () => [],
    },
    initiallySelectedCareType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoadingMap: true,
      isLoadingResults: true,
      locationData: [],
      locationResults: [],
      locationResultsSplitted: {},
      locationMarkerData: [],
      locationMarkerRefs: [],
      map: {
        showMap: true,
        center: LeafLet.latLng(0, 0),
        url: `https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${this.stadiaMapsApiKey}`,
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        icon: normalIcon,
        locationIcon: currentLocationIcon,
        clusterOptions: {
          iconCreateFunction: (cluster) => {
            return LeafLet.divIcon({
              html: '<span class="marker-cluster-inner">' + cluster.getChildCount() + '</span>',
              iconSize: new LeafLet.Point(46, 46),
              className: 'marker-cluster',
            });
          },
        },
        mapOptions: {
          maxZoom: 16,
          minZoom: 9,
          center: LeafLet.latLng(52.0987495, 5.1163504),
        },
        showCurrentLocation: 0,
        bounds: null,
        currentBounds: null,
      },
      tabIsActive: {
        childcareTypeKey: 'Daycare',
        childcareTypeName: 'Kinderdagverblijf',
      },
      mapIsActive: true,
      languageNotAvailableModalIsOpen: false,
      languageNotAvailableModalVenueObject: { url: '', id: '' },
      trackEvent,
    };
  },
  computed: {
    currentPosition() {
      return { lat: this.latlng.latitude, lng: this.latlng.longitude };
    },
    trackData() {
      return {
        event: 'childcare_location',
        event_name: 'childcare_location',
        event_category: 'registration',
        event_action: 'chose_childcare_location',
        event_label: this.tabIsActive.childcareTypeName,
        location: this.locatorPostalCode,
        school: '',
        count_searchresult: this.locationResults.length,
      };
    },
  },
  async mounted() {
    if (this.apiEndpoint) {
      await this.fetchLocations();
    } else {
      console.warn(`No (valid) api endpoint provided: [${this.apiEndpoint}]`);
      this.isLoadingMap = false;
    }
    if (typeof window !== 'undefined') {
      this.mapIsActive = window.innerWidth >= 1024;
    }
  },
  methods: {
    async fetchLocations() {
      const token = document.body.querySelector(
        'input[type=hidden][name=__RequestVerificationToken]',
      )?.value;
      const config = { headers: { 'X-REQUEST-VERIFICATION-TOKEN': token } };

      try {
        const { status, data } = await axios.get(this.apiEndpoint, config);
        if (status === 200 && data?.locations?.length > 0) {
          // Pre-process each location: compute its care types once
          this.locationData = data.locations
            .map((location) => {
              // Compute a Set of all care types available for the location
              const computedCareTypes = new Set();
              location.venues.forEach((venue) => {
                (venue.childCareType || []).forEach((type) => computedCareTypes.add(type));
              });
              location.computedCareTypes = computedCareTypes;
              return location;
            })
            .filter((location) => this.isCorrectCareType(location));

          // Determine markers and results based on whether latlng or areaId is provided
          let results = [];
          const boundingBox = [];

          if (this.latlng && this.latlng.latitude && this.latlng.longitude) {
            // Compute distance for each location
            this.locationData.forEach((location) => {
              const { latitude, longitude } = location.geoLocation;
              location.distance = this.getDistance(
                this.latlng.latitude,
                this.latlng.longitude,
                latitude,
                longitude,
              );
            });

            // Try to get locations within 1.5, 3, then 5 km radius
            let tmpResults = this.getLocationsByDistance(1.5);
            if (tmpResults.length < 3) {
              tmpResults = this.getLocationsByDistance(3);
            }
            if (tmpResults.length < 3) {
              tmpResults = this.getLocationsByDistance(5);
            }
            if (tmpResults.length > 0) {
              boundingBox.push([this.currentPosition.lat, this.currentPosition.lng]);
              tmpResults.forEach((result) => {
                boundingBox.push([result.geoLocation.latitude, result.geoLocation.longitude]);
                results.push(result);
              });
            } else {
              // No results found: use zipcode latlng
              const latLng = new LeafLet.latLng(this.latlng.latitude, this.latlng.longitude);
              this.$refs.map.setCenter(latLng);
              this.map.center = latLng;
              const bounds = latLng.toBounds(5000);
              this.$refs.map.fitBounds(bounds);
            }
            this.locationResults = this.sortOnDistance(results);
            this.locationMarkerData = results;
          } else if (this.areaId) {
            this.locationData.forEach((location) => {
              if (location.areaIds.includes(this.areaId)) {
                const { latitude, longitude } = location.geoLocation;
                boundingBox.push([latitude, longitude]);
                results.push(location);
              }
            });
            this.locationResults = results;
            this.locationMarkerData = results;
          } else {
            // Show all locations
            this.locationData.forEach((location) => {
              const { latitude, longitude } = location.geoLocation;
              boundingBox.push([latitude, longitude]);
              results.push(location);
            });
            this.locationResults = results;
          }
          this.map.bounds = LeafLet.latLngBounds(boundingBox);
          // Initialize active tab
          this.handleMapResultsSwitcher(this.initiallySelectedCareType);
          this.$nextTick(this.updateMarkerRefs);
        }
      } catch (e) {
        console.error(e);
      }
      this.isLoadingMap = false;
      this.map.showCurrentLocation = 1;
    },
    handleMapResultsSwitcher(careTypeKey) {
      const found = this.careTypes.find((careType) => careType.childcareTypeKey === careTypeKey);
      if (found) {
        this.tabIsActive = found;
      }
    },
    boundsUpdate(bounds) {
      const { _southWest, _northEast } = bounds;

      // prevent data loss when occassionally the bounds are the same
      if (_southWest?.lat === _northEast?.lat && _southWest?.lng === _northEast?.lng) {
        return;
      }

      const results = [];
      this.map.currentBounds = bounds;
      this.locationData.forEach((location) => {
        const { latitude, longitude } = location.geoLocation;
        if (this.areaId) {
          if (
            location.areaIds.includes(this.areaId) &&
            this.isInBoundingBox({ latitude, longitude }, _southWest, _northEast)
          ) {
            if (location.distance) delete location.distance;
            results.push(location);
          }
        } else {
          if (this.isInBoundingBox({ latitude, longitude }, _southWest, _northEast)) {
            if (this.latlng && this.latlng.latitude) {
              location.distance = this.getDistance(
                this.latlng.latitude,
                this.latlng.longitude,
                latitude,
                longitude,
              );
            }
            results.push(location);
          }
        }
      });
      const sortedResults = this.sortOnDistance(results);
      this.locationResults = sortedResults;
      this.locationResultsSplitted = this.splitLocationResults(sortedResults);
      this.isLoadingResults = false;
      if (this.latlng && this.latlng.latitude) {
        this.locationMarkerData = results;
      }
      this.$nextTick(this.updateMarkerRefs);
    },
    updateMarkerRefs() {
      if (this.$refs.markersRef) {
        this.locationMarkerRefs = this.$refs.markersRef.map((ref) => ({
          lat: ref.latLng.lat,
          lng: ref.latLng.lng,
          icon: ref.icon,
          mapObject: ref.mapObject,
        }));
      }
    },
    getLocationsByDistance(distance) {
      return this.locationData.filter((location) => location.distance < distance);
    },
    isCorrectCareType(location) {
      if (location.computedCareTypes) {
        return this.careTypes.some((ct) => location.computedCareTypes.has(ct.childcareTypeKey));
      }
      return false;
    },
    async selectLocation(marker) {
      const currentLocation = this.getLocationFromResults(marker);
      if (currentLocation) {
        if (currentLocation.isSelected) {
          currentLocation.isSelected = false;
        } else {
          await this.deselectLocations();
          currentLocation.isSelected = true;
        }
      }
      this.boundsUpdate(this.map.currentBounds);
    },
    deselectLocations() {
      return new Promise((resolve) => {
        this.locationResults.forEach((location) => {
          location.isSelected = false;
        });
        resolve();
      });
    },
    getLocationFromResults(marker) {
      const { latitude, longitude } = marker.geoLocation;
      return (
        this.locationResults.find(
          (location) =>
            location.geoLocation.latitude === latitude &&
            location.geoLocation.longitude === longitude,
        ) || null
      );
    },
    showMarkerIfCorrectCaretype(marker) {
      let computed = new Set();
      marker.venues.forEach((venue) => {
        (venue.childCareType || []).forEach((type) => computed.add(type));
      });
      return computed.has(this.tabIsActive.childcareTypeKey);
    },
    selectMarker(location) {
      const currentMarker = this.getMarkerRef(location);
      if (currentMarker) {
        currentMarker.mapObject.setIcon(bigIcon);
      }
    },
    deselectMarker(location) {
      const currentMarker = this.getMarkerRef(location);
      if (currentMarker) {
        currentMarker.mapObject.setIcon(normalIcon);
      }
    },
    getMarkerRef(location) {
      const { latitude, longitude } = location.geoLocation;
      return (
        this.locationMarkerRefs.find(
          (marker) => marker.lat === latitude && marker.lng === longitude,
        ) || null
      );
    },
    toggleMobileMap() {
      this.mapIsActive = !this.mapIsActive;
    },
    isActiveCareType(venue) {
      return (venue.childCareType || []).includes(this.tabIsActive.childcareTypeKey);
    },
    isInBoundingBox(latlng, sw, ne) {
      return (
        latlng.latitude <= ne.lat &&
        latlng.latitude >= sw.lat &&
        latlng.longitude <= ne.lng &&
        latlng.longitude >= sw.lng
      );
    },
    getDistance(startLat, startLon, destinationLat, destinationLng) {
      const R = 6371;
      const dLat = this.toRad(destinationLat - startLat);
      const dLon = this.toRad(destinationLng - startLon);
      const lat1 = this.toRad(startLat);
      const lat2 = this.toRad(destinationLat);
      const a = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return this.round(R * c, 1);
    },
    toRad(Value) {
      return (Value * Math.PI) / 180;
    },
    round(value, precision) {
      const multiplier = 10 ** (precision || 0);
      return Math.round(value * multiplier) / multiplier;
    },
    sortOnDistance(array, order = 'ASC') {
      return array.sort((a, b) => {
        if (a.distance < b.distance) return order === 'ASC' ? -1 : 1;
        if (a.distance > b.distance) return order === 'ASC' ? 1 : -1;
        return 0;
      });
    },
    splitLocationResults(locationResults) {
      const result = {};
      // Initialize groups based on careTypes
      this.careTypes.forEach((careType) => {
        result[careType.childcareTypeKey] = [];
      });
      // Group each location by its available care types (avoiding duplicates)
      locationResults.forEach((item) => {
        const added = new Set();
        item.venues.forEach((venue) => {
          (venue.childCareType || []).forEach((type) => {
            if (
              this.careTypes.find((careType) => careType.childcareTypeKey === type) &&
              !added.has(type)
            ) {
              result[type].push(item);
              added.add(type);
            }
          });
        });
      });
      return result;
    },
    getVenueBasedOnCareType(marker) {
      const childCareType = this.tabIsActive.childcareTypeKey;
      if (!childCareType) return {};
      const matched = marker.venues.filter((venue) =>
        (venue.childCareType || []).includes(childCareType),
      );
      return matched[0] || {};
    },
    closeLeafletPopup() {
      if (this.$refs.map && this.$refs.map.mapObject) {
        this.$refs.map.mapObject.closePopup();
      }
    },
    getLocalizedUrl(venue) {
      if (!venue || !Object.keys(venue).length) return;
      return venue.localizedUrls[document.documentElement.lang] || venue.url;
    },
    setLanguageNotAvailableModal(givenObject) {
      const object = givenObject || {};
      this.languageNotAvailableModalIsOpen = object.isOpen || false;
      this.languageNotAvailableModalVenueObject.url = object.url || '';
      this.languageNotAvailableModalVenueObject.id = object.id || '';
    },
    manageOtherLanguageModal(venue, location) {
      if (!venue || !Object.keys(venue).length) return;
      const currentLanguage = document.documentElement.lang;
      const url = this.getLocalizedUrl(venue);
      const goToUrl = () => {
        if (location?.address?.addressLine) {
          this.trackEvents(venue, location);
        }
        window.location = url + `?zipcode=${this.locatorPostalCode}`;
      };
      if (currentLanguage === 'nl') {
        goToUrl();
        return;
      }
      if (!url.includes(`/${currentLanguage}/`)) {
        this.setLanguageNotAvailableModal({
          isOpen: true,
          url: url,
          id: venue?.id,
        });
        return;
      }
      goToUrl();
    },
    trackEvents(venue, location) {
      const gtmData = {
        ...this.trackData,
        school: venue.childcareResultLocationName,
        location: location.name,
      };
      trackEvent(gtmData, this.$gtm.enabled());
      trackEvent(
        {
          event: 'registration',
          event_name: 'chooseChildcareLocation',
          event_category: 'childcareLocation',
          event_action: 'chooseChildcareLocation',
          childcare_type: setDayCareString(venue.childCareType[0]),
          location_adres: location.address.addressLine,
          location_place: location.address.city,
          location_province: location.address.province,
          mdm_id: venue.mdmId,
        },
        this.$gtm.enabled(),
      );
    },
  },
};
</script>

<style lang="postcss">
.lr-map {
  &-title,
  &-intro {
    @apply text-center;
  }

  &-container {
    @apply flex flex-col overflow-hidden rounded-smd mb-7;
    @screen lg {
      @apply flex-row mb-0;
      height: 700px;
    }
    &-left {
      @apply w-full;
      @screen lg {
        @apply h-full w-2/5;
      }
    }
    &-right {
      @apply w-full;
      height: 700px;
      @screen lg {
        @apply w-3/5 h-full;
      }
      &.is-hidden {
        @apply hidden;
        @screen lg {
          @apply block;
        }
      }
    }
    .leaflet-marker-icon {
      &.marker-cluster {
        @apply flex justify-center items-center bg-no-repeat;
        background-image: url('~@/assets/svg/map/map-cluster.svg?inline');
        background-size: 110%;
        background-position: 50% 0;
        .marker-cluster-inner {
          @apply text-lg font-bold;
        }
      }
    }
  }
  &-results {
    @apply flex flex-col h-full;
    &-nav {
      @apply flex;
    }
    &-btn {
      @apply p-5 text-sm w-1/2;
      &.is-active {
        @apply bg-gray-50;
      }
    }
    &-list {
      @apply hidden relative border-b border-gray-50;
      &-item {
        @apply flex p-8 pt-4;
        &.is-selected {
          a {
            @apply text-black;
          }
          .icon {
            @apply text-black transform translate-x-2;
          }
        }
        > h4 {
          @apply w-full;
        }
      }
      &.is-visible {
        @apply flex flex-col;
      }
      &-container {
        @apply h-full bg-gray-50 overflow-auto;
        &.is-hidden {
          @apply hidden;
          @screen lg {
            @apply block;
          }
        }
      }
    }
    &-link {
      @apply flex flex-col no-underline text-primary-bravo;
      &:hover {
        @apply text-black;
        .icon {
          @apply text-black transform translate-x-2;
        }
      }
      .icon {
        @apply text-primary-bravo fill-current mr-4 w-4;
        transition: color 300ms, transform 300ms;
      }
    }
    &-inner-title {
      @apply flex gap-4;
      &:not(:last-child) {
        @apply mb-6;
      }
    }
    &-distance {
      @apply absolute top-5 right-8 font-sans text-sm font-normal text-gray-500;
    }
    &-images {
      @apply flex flex-wrap justify-between;
    }
    &-image {
      @apply block object-cover object-center;
      height: 120px;
      width: 120px;
      @screen lg {
        @apply w-full;
        height: 120px;
      }
      &-container {
        @apply overflow-hidden;
        width: 48%;
      }
    }
  }
  &-toggle-btn {
    @apply block mx-auto;
    @screen lg {
      @apply hidden;
    }
  }
  .loader {
    z-index: 400;
  }
}

.leaflet-container {
  .leaflet-popup-content {
    margin: 0;
    width: 230px;
    overflow-x: hidden;
  }
  a.leaflet-popup-close-button {
    top: 4px;
    right: 4px;
  }
}

.marker-popup {
  @apply font-sans p-5;
  &-link {
    @apply no-underline;
    &:hover {
      .marker-popup-title {
        @apply text-black;
      }
    }
  }
  &-title {
    @apply text-primary-bravo text-base mb-2;
  }
  &-meta {
    @apply flex text-gray-500 text-sm mb-2;
  }
  &-city {
    @apply block mr-4;
  }
  &-distance {
    @apply block mr-4;
  }
}
</style>
